import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import AppConfig from '~/utils/appconfig';

/** A table of the selected dose: its statistics per structure, or its optimization objectives. */
export type DoseTable = 'statistics' | 'objectives';

export interface DoseTableSelection {
    /** The table shown. */
    readonly table: DoseTable;
    readonly selectTable: (table: DoseTable) => void;
    /** Whether the objectives table is a choice at all; without it there is nothing to choose. */
    readonly objectivesOffered: boolean;
    /** Whether the selected dose has objectives; without them their table cannot be shown. */
    readonly objectivesAvailable: boolean;
}

/**
 * Which table of the selected dose is shown, the statistics at first. The
 * objectives table is a choice only with the optimization objectives
 * feature, and is shown only while the selected dose has objectives: when
 * they go, the selection returns to the statistics and stays there until
 * the user chooses again.
 */
export function useDoseTableSelection(): DoseTableSelection {
    const objectivesOffered = AppConfig.isFeatureEnabled(
        Features.OPTIMIZATION_OBJECTIVES
    );
    const objectivesAvailable =
        useRecoilValue(optimizationObjectivesState).length > 0;
    const [table, setTable] = useState<DoseTable>('statistics');
    if (table === 'objectives' && !(objectivesOffered && objectivesAvailable)) {
        setTable('statistics');
    }
    return {
        table,
        selectTable: setTable,
        objectivesOffered,
        objectivesAvailable,
    };
}
